/**
 * API 錯誤 → 使用者可見訊息的「轉譯層」。
 *
 * 原則:**不把原始錯誤直接呈現給使用者。**
 * - 系統/認證/網路錯誤(5xx / 401 / 403 / 逾時 / 斷線):一律用前端制式文案,
 *   絕不顯示後端 body(可能含 stack trace、SQL、內部路徑等敏感/技術資訊)。
 * - 業務性 4xx(400 / 409 / 422):這類錯誤後端「刻意」回給使用者看(如刪除阻擋原因、
 *   逐欄驗證訊息、動態內容),才採用後端 message;沒有則給該狀態的制式文案。
 * - 原始錯誤一律 console.error 保留給開發者除錯(見 api_helper 攔截器)。
 *
 * 未來:後端 Swagger 若提供錯誤 `code`,改為 code → i18n 對照(比信任 message 文字更穩)。
 */

const GENERIC = "系統發生錯誤,請稍後再試";

// 網路層文案輸出成常數:防護測試(hybridPassthrough)以此判斷「請求是否出網」,
// 引用同一份常數,改文案不會讓防護測試靜默失明
export const NETWORK_ERROR_MSG = "網路連線異常,請檢查網路後再試";
export const TIMEOUT_MSG = "連線逾時,請稍後再試";
export const NOT_IMPLEMENTED_MSG = "此功能尚未串接正式後端";

// 後端 message 僅在這些「業務性」狀態才可信任呈現
const TRUST_BACKEND_MESSAGE = new Set([400, 409, 422]);

// 制式文案(不使用後端 body)
const BY_STATUS = {
  400: "資料格式有誤,請確認後再試",
  401: "登入已失效,請重新登入",
  403: "您沒有權限執行此操作",
  404: "查無資料",
  408: TIMEOUT_MSG,
  409: "操作衝突,請重新整理後再試",
  422: "資料驗證未通過,請確認欄位",
  429: "操作太頻繁,請稍後再試",
};

/**
 * 業務性 4xx 可信任呈現的後端訊息:
 * - { message: string }(既有慣例)
 * - ProblemDetails { Detail / detail: string }(ASP.NET 標準;swagger 37 支 400 用這個)
 * - string[](Backend v1.00 的 Create/Update/ChangePassword 逐欄驗證),以換行合併
 * 純字串 body 仍不信任(可能是技術訊息,維持制式文案)。
 */
function extractBusinessMessage(data) {
  if (data && typeof data.message === "string") return data.message.trim();
  const detail = data?.Detail ?? data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail.trim();
  if (Array.isArray(data) && data.length && data.every((s) => typeof s === "string")) {
    return data.map((s) => s.trim()).filter(Boolean).join("\n");
  }
  return "";
}

/**
 * @param {any} error axios 攔截器收到的錯誤物件
 * @returns {string} 可安全顯示給使用者的訊息
 */
export function translateApiError(error) {
  // 1) 沒有 response = 網路層錯誤(斷線/逾時/CORS…),不可能有可信 body
  if (!error || !error.response) {
    if (error && (error.code === "ECONNABORTED" || /timeout/i.test(error.message || ""))) {
      return TIMEOUT_MSG;
    }
    return NETWORK_ERROR_MSG;
  }

  const { status } = error.response;
  const backendMsg = extractBusinessMessage(error.response.data);

  // 2) 5xx:一律制式,絕不顯示後端 body。
  // 例外 501:USE_MOCK=false 時未串接模組的前端守衛(mock/notImplemented)用它
  // 誠實說明,不能講成「系統忙碌」誤導使用者以為是後端壞了
  if (status === 501) return NOT_IMPLEMENTED_MSG;
  if (status >= 500) return "系統忙碌中,請稍後再試";

  // 3) 業務性 4xx:採用後端刻意提供的訊息(如刪除阻擋原因)
  if (TRUST_BACKEND_MESSAGE.has(status) && backendMsg) return backendMsg;
  // 3b) 呼叫端明確標 `trustStringError` 的請求(例:DcImport 的 400 swagger 就是 string,
  //     內容是「找不到資產編號欄」這種給人看的原因):純字串 body 也採用,但截斷、去標籤,
  //     避免整段 stack trace 直出。預設仍不信任純字串(見 extractBusinessMessage)。
  const trustString = !!(error.config && error.config.trustStringError);
  if (trustString && TRUST_BACKEND_MESSAGE.has(status) && typeof error.response.data === "string") {
    const text = error.response.data.replace(/<[^>]+>/g, "").trim();
    if (text) return text.length > 300 ? `${text.slice(0, 300)}…` : text;
  }

  // 4) 其餘依狀態制式文案
  return BY_STATUS[status] || GENERIC;
}

export default translateApiError;
